import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { ApolloError } from '@apollo/client';
import MessagePage from '@dashboard/components/pages/MessagePage';
import { alertError } from '@dashboard/contexts/SnackbarManager/store';
import { useAcceptPlatformInviteMutation, useInviteQuery } from '@dashboard/generated/wp/authentication';
import { Button } from '@mui/material';

type Props = {
  inviteSecret: string;
  platformUserId: string;
};

export default function InvitePage({ platformUserId, inviteSecret }: Props): JsxNode {
  const navigate = useNavigate();

  const [acceptInvite, { loading }] = useAcceptPlatformInviteMutation();
  const { data } = useInviteQuery({ variables: { platformUserId, inviteSecret } });

  const invite = data?.invite;

  if (invite === undefined) {
    return null;
  }

  if (invite === null) {
    return <Navigate to="/" />;
  }

  async function onClick() {
    try {
      const { data } = await acceptInvite({ variables: { inviteSecret, platformUserId } });
      const id = data?.acceptPlatformInvite?.id;
      if (id) {
        navigate(`/o/${id}`);
      }
    } catch (e) {
      if (e instanceof ApolloError) {
        alertError(e.message);
      } else {
        alertError('Error accepting invite');
      }
    }
  }

  return (
    <MessagePage
      icon="PLANTS"
      title={`You've been invited to join "${invite.name}"`}
      description="As a collaborator on this Waypoint workspace, you'll be able to build and monitor email notifications with your team."
      action={
        <Button size="large" disabled={loading} onClick={onClick} variant="contained">
          Accept and join workspace
        </Button>
      }
    />
  );
}
